import React from "react";

import AsyncImage from "@appComponents/AsyncImage/AsyncImage";
import noPhotoCompany from "@images/no_photo_company.png";
import { formatTimeRemaining } from "./formatTimeRemaining";
import moment from "moment";
import { formatNumberToUSD } from "@features/utils/functions";
import { Pitch } from "@services/Pitches.service";
import { WORDPRESS_URL } from "@bundles/consts";
import { Button } from "@appComponents/Button";
import classNames from "classnames";
import ChevronDownIcon from "../../../icons/chevron-down-black.svg";

export const overviewText = (
  <>
    <p>
      In a world of financial uncertainty, BnkToTheFuture offers qualifying
      investors the opportunity to invest in the future of finance and
      technology. Traditional banks have a history of mismanaging funds,
      manipulating interest rates, and prioritising their own survival over
      customers’ financial well-being. In contrast, BnkToTheFuture provides
      access to innovative financial products, companies, and funds that promote
      financial inclusion, lower fees, and investor ownership of assets.
    </p>

    <p className="mb-0">
      Our platform is designed for those looking to allocate a portion of their
      wealth to high-risk, high-reward opportunities that have the potential to
      reshape finance. It’s free to join, and qualifying investors can access
      exclusive deals, news, and updates. Sign up today and take the first step
      towards building a future-focused investment portfolio.
    </p>
  </>
);

export const faq = [
  {
    title: "What is the minimum investment amount?",
    description: <p className="ml-8">The minimum investment is $1,000.</p>,
  },
  {
    title: "What are A shares and B shares?",
    description: (
      <p className="ml-8">
        Both A and B shares represent ownership in the investee entity, which
        may be equity, convertible notes, or other financial instruments. The
        key difference is that A shares carry voting rights, while B shares do
        not. This distinction is important when the SP (Segregated Portfolio)
        votes on corporate actions such as exit opportunities or buybacks.
      </p>
    ),
  },
  {
    title: "How are my shares held?",
    description: (
      <p className="ml-8">
        BnkToTheFuture (BF) operates under its umbrella company, Bnk To The
        Future Capital SPC, based in the Cayman Islands. For each investment
        opportunity, a separate Segregated Portfolio (SP) is created to hold the
        investee entity’s shares or other financial instruments. When investing
        through BF, users receive A or B shares in the SP, which in turn owns
        shares in the underlying company. Each SP is legally distinct, meaning
        that if one investee company fails, only its corresponding SP is
        affected, ensuring the security of other investments.
      </p>
    ),
  },
  {
    title: "What fees does BF charge?",
    description: (
      <p className="ml-8">
        There may be a transaction fee when investing, but using your USDT
        internal balance is free as it is considered an internal transfer. BF
        charges a 5% fee on dividends/distributions and a 5% fee on exit events
        or share distributions. For full details, please refer to our{" "}
        <a href={`${WORDPRESS_URL}/fees`} className="font-medium underline">
          fee section.
        </a>
      </p>
    ),
  },
  {
    title: "Will the company I invest in be listed on the secondary market?",
    description: (
      <p className="ml-8">
        There is no guarantee, but BF aims to list companies that still hold
        value and potential on our secondary market. This provides early
        liquidity for investors who wish to sell and opportunities for others
        looking to adjust their portfolios.
      </p>
    ),
  },
];

type GetPitchesColumnsArgs = {
  isMobile: boolean;
  handleExpandToggle: (record: Pitch) => void;
  expandedRowKeys: React.Key[];
};

export const getPitchesColumns = ({
  isMobile,
  handleExpandToggle,
  expandedRowKeys,
}: GetPitchesColumnsArgs) =>
  isMobile
    ? [
        {
          title: "NAME",
          dataIndex: "title",
          key: "title",
          render: (_, pitch: Pitch) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage image={pitch.image} fallback={noPhotoCompany}>
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <a href={`/pitches/${pitch.slug}`}>
                  <span>{pitch.title}</span>
                </a>
              </div>
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          width: 10,
          render: (_, pitch: Pitch) => {
            return (
              <Button isSmall onClick={() => handleExpandToggle(pitch)}>
                <span
                  className={classNames({
                    "rotate-180": expandedRowKeys.includes(pitch.id),
                  })}
                >
                  <ChevronDownIcon />
                </span>
              </Button>
            );
          },
        },
      ]
    : [
        {
          title: "NAME",
          dataIndex: "title",
          key: "title",
          sorter: (a, b) => a.title?.localeCompare(b.title),
          render: (_, pitch: Pitch) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage image={pitch.image} fallback={noPhotoCompany}>
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <a href={`/pitches/${pitch.slug}`}>
                  <span>{pitch.title}</span>
                </a>
              </div>
            );
          },
        },
        {
          title: "TIME REMAINING",
          dataIndex: "time_remaining",
          key: "time_remaining",
          sorter: (a, b) =>
            Number(a.time_remaining ?? 0) - Number(b.time_remaining ?? 0),
          render: (timeRemaining: number) => {
            return timeRemaining > 0
              ? formatTimeRemaining(timeRemaining)
              : "Finished";
          },
        },
        {
          title: "TYPE",
          dataIndex: "specialisation_type",
          key: "specialisation_type",
          sorter: (a, b) =>
            a.specialisation_type?.localeCompare(b.specialisation_type),
          render: (specialisationType: string | number) => {
            if (!specialisationType) {
              return "-";
            }

            return specialisationType;
          },
        },
        {
          title: "PITCH DATE",
          dataIndex: "pitch_date",
          key: "pitch_date",
          sorter: (a, b) =>
            moment(a.pitch_date).valueOf() - moment(b.pitch_date).valueOf(),
          render: (pitch_date: string) => {
            if (!pitch_date) return "-";
            return moment(pitch_date).format("MMM DD, yyyy");
          },
        },
        {
          title: "NO. OF INVESTORS",
          dataIndex: "number_of_investors",
          key: "number_of_investors",
          sorter: (a, b) =>
            Number(a.number_of_investors ?? 0) -
            Number(b.number_of_investors ?? 0),
        },
        {
          title: "RAISED %",
          dataIndex: "raised_percent",
          key: "raised_percent",
          sorter: (a, b) =>
            Number(a.raised_percent ?? 0) - Number(b.raised_percent ?? 0),
          render: (raisedPercent: string) => {
            return (
              <div>
                <div className="text-[#81109C] mb-[7px]">
                  {Number(raisedPercent ?? 0).toFixed(0)} %
                </div>
                <div
                  className="h-[6px] rounded-[10px] bg-[#81109C]"
                  style={{ width: `${Number(raisedPercent ?? 0)}%` }}
                ></div>
              </div>
            );
          },
        },
        {
          title: "RAISED $",
          dataIndex: "raised_amount",
          key: "raised_amount",
          align: "right" as const,
          sorter: (a, b) =>
            Number(a.raised_amount ?? 0) - Number(b.raised_amount ?? 0),
          render: (_, pitch: Pitch) => {
            if (
              Number(pitch.raised_amount ?? 0) <
              Number(pitch.expected_amount ?? 0)
            ) {
              return (
                <div>
                  {formatNumberToUSD(Number(pitch.raised_amount ?? 0))}
                  <span className="text-grey-lightest">
                    &nbsp;of{" "}
                    {formatNumberToUSD(Number(pitch.expected_amount ?? 0))}
                  </span>
                </div>
              );
            } else {
              return formatNumberToUSD(Number(pitch.raised_amount ?? 0));
            }
          },
        },
      ];
